import {defineMessages} from 'react-intl'

/**
 * Catálogo de Facturación (`/billing`). Namespace **`telephony.billing.*`**, no
 * `admin.*`: la pantalla es telefonía —números, minutos, coste de Twilio— y
 * comparte hub con `twilioBilling/`, que ya vive en esa familia. Se migró en la
 * 1.11.e solo porque quedó ahí en el reparto de fases (plan §5.7).
 *
 * No se traducen: los E.164, los nombres de agente y de propietario, los
 * topónimos de `US_STATES`, ni los códigos de clasificación que viajan a la API
 * (`pool`, `policydial`, `messaging`) — solo sus etiquetas.
 */
export const BILLING_MESSAGES = defineMessages({
  // ── Pestañas del hub ──
  tabCost: {id: 'telephony.billing.tab.cost', defaultMessage: 'Cost & Usage'},
  tabInventory: {id: 'telephony.billing.tab.inventory', defaultMessage: 'Inventory numbers'},
  tabUsage: {id: 'telephony.billing.usage.title', defaultMessage: 'Consumption by Number'},
  tabRebilling: {id: 'telephony.billing.tab.rebilling', defaultMessage: 'Rebilling'},
  tabAssignments: {id: 'telephony.billing.tab.assignments', defaultMessage: 'Assignments'},

  // ── Refacturación ──
  rebillingTitle: {id: 'telephony.billing.rebilling.title', defaultMessage: 'Agent Rebilling'},
  rebillingSubtitle: {
    id: 'telephony.billing.rebilling.subtitle',
    defaultMessage: 'Charge agents for their personal numbers (rental + voice) with a margin.',
  },
  markups: {id: 'telephony.billing.rebilling.markups', defaultMessage: 'Markups'},
  markupPercent: {id: 'telephony.billing.rebilling.markupPercent', defaultMessage: 'Markup (%)'},
  /** `{base}` es el alquiler base ya formateado como importe. */
  markupApprox: {
    id: 'telephony.billing.rebilling.markupApprox',
    defaultMessage: '≈ $ (per ${base} rental)',
  },
  saveMarkup: {id: 'telephony.billing.rebilling.saveMarkup', defaultMessage: 'Save markup'},
  markupHint: {
    id: 'telephony.billing.rebilling.markupHint',
    defaultMessage:
      'One markup — % and $ stay in sync — applied to <b>both</b> rental and voice: charge = cost × (1 + markup%). Global default.',
  },
  toastMarkupSaved: {
    id: 'telephony.billing.rebilling.toast.saved',
    defaultMessage: 'Markup saved.',
  },
  byAgentThisMonth: {
    id: 'telephony.billing.rebilling.byAgent',
    defaultMessage: 'By agent — this month',
  },
  colTimePrice: {id: 'telephony.billing.rebilling.col.timePrice', defaultMessage: 'Time / Price'},
  colCostRent: {id: 'telephony.billing.rebilling.col.costRent', defaultMessage: 'Cost rent'},
  colAgentCharge: {
    id: 'telephony.billing.rebilling.col.agentCharge',
    defaultMessage: 'Agent charge',
  },
  colMargin: {id: 'telephony.billing.rebilling.col.margin', defaultMessage: 'Margin'},
  /** `{minutes}` ya viene formateado; el importe va aparte. */
  minutesAndAmount: {
    id: 'telephony.billing.rebilling.minutesAmount',
    defaultMessage: '{minutes} min · {amount}',
  },
  callCount: {
    id: 'telephony.billing.rebilling.callCount',
    defaultMessage: '{count, plural, one {# call} other {# calls}}',
  },
  emptyAgents: {
    id: 'telephony.billing.rebilling.empty',
    defaultMessage: 'No agent numbers billed this month yet.',
  },
  detailTitle: {
    id: 'telephony.billing.rebilling.detail.title',
    defaultMessage: 'Call consumption — {agent}',
  },
  detailSummary: {
    id: 'telephony.billing.rebilling.detail.summary',
    defaultMessage: ' · {count} calls · {amount}',
  },
  emptyCalls: {
    id: 'telephony.billing.rebilling.detail.empty',
    defaultMessage: 'No calls this month.',
  },
  colWhen: {id: 'telephony.autoPush.audit.column.when', defaultMessage: 'When'},
  colDir: {id: 'telephony.billing.rebilling.col.dir', defaultMessage: 'Dir'},
  colMyNumber: {id: 'telephony.billing.rebilling.col.myNumber', defaultMessage: 'My number'},
  colOtherParty: {
    id: 'telephony.billing.rebilling.col.otherParty',
    defaultMessage: 'Other party',
  },
  colTime: {id: 'telephony.billing.rebilling.col.time', defaultMessage: 'Time'},
  colPrice: {id: 'telephony.billing.rebilling.col.price', defaultMessage: 'Price'},
  directionInbound: {
    id: 'telephony.supervisor.report.group.inbound',
    defaultMessage: 'Inbound',
  },
  directionOutbound: {
    id: 'telephony.supervisor.report.group.outbound',
    defaultMessage: 'Outbound',
  },
  /** Duración en segundos: `45s`. */
  seconds: {id: 'telephony.billing.rebilling.seconds', defaultMessage: '{seconds}s'},

  // ── Consumo por número ──
  usageTitle: {id: 'telephony.billing.usage.title', defaultMessage: 'Consumption by Number'},
  usageSubtitle: {
    id: 'telephony.billing.usage.subtitle',
    defaultMessage:
      'Voice usage & Twilio cost per number — every owned number (pool, PolicyDial, messaging).',
  },
  usageHint: {
    id: 'telephony.billing.usage.hint',
    defaultMessage:
      'Compiled in the background from Twilio. A fresh month may be empty until the first build — click “{action}”, then reopen in ~1 min.',
  },
  rebuildData: {id: 'telephony.supervisor.cid.rebuild', defaultMessage: 'Rebuild data'},
  rebuildTitle: {
    id: 'telephony.billing.usage.rebuildTitle',
    defaultMessage: 'Recompile this month’s usage from Twilio (runs in the background)',
  },
  queuing: {id: 'telephony.supervisor.cid.queuing', defaultMessage: 'Queuing…'},
  byNumber: {id: 'telephony.billing.usage.byNumber', defaultMessage: 'By number'},
  searchNumber: {
    id: 'telephony.billing.inventory.searchPlaceholder',
    defaultMessage: 'Search number…',
  },
  allTypes: {id: 'telephony.billing.usage.allTypes', defaultMessage: 'All types'},
  classPool: {id: 'telephony.billing.usage.class.pool', defaultMessage: 'Pool'},
  classMessaging: {
    id: 'telephony.billing.usage.class.messaging',
    defaultMessage: 'Messaging',
  },
  classUnclassified: {
    id: 'telephony.billing.inventory.class.unclassified',
    defaultMessage: 'Unclassified',
  },
  colIn: {id: 'telephony.billing.usage.col.in', defaultMessage: 'In'},
  colOut: {id: 'telephony.billing.usage.col.out', defaultMessage: 'Out'},
  colMinutes: {id: 'telephony.billing.usage.col.minutes', defaultMessage: 'Minutes'},
  usageEmpty: {
    id: 'telephony.billing.usage.empty',
    defaultMessage: 'No numbers with activity for this period.',
  },
  pageSummary: {
    id: 'telephony.billing.usage.pageSummary',
    defaultMessage: '{count} numbers · page {current} of {total}',
  },
  prev: {id: 'telephony.billing.usage.prev', defaultMessage: 'Prev'},

  // ── Asignaciones ──
  assignmentsTitle: {
    id: 'telephony.billing.assignments.title',
    defaultMessage: 'Number Assignments',
  },
  assignmentsSubtitle: {
    id: 'telephony.billing.assignments.subtitle',
    defaultMessage:
      'Reassign where a number rings and who dials out with it. The buyer (owner) keeps being billed.',
  },
  colOwnerPays: {
    id: 'telephony.billing.assignments.col.ownerPays',
    defaultMessage: 'Owner (pays)',
  },
  colAssignedTo: {
    id: 'telephony.numbers.column.assignedTo',
    defaultMessage: 'Assigned to',
  },
  colMode: {id: 'telephony.billing.assignments.col.mode', defaultMessage: 'Mode'},
  groupBadge: {
    id: 'telephony.billing.assignments.groupBadge',
    defaultMessage: 'Group: {name}',
  },
  assignmentsEmpty: {
    id: 'telephony.billing.assignments.empty',
    defaultMessage: 'No numbers in your assignment scope.',
  },

  errSaveMarkup: {
    id: 'telephony.billing.rebilling.error.saveMarkup',
    defaultMessage: 'Could not save markup',
  },
  toastQueued: {
    id: 'telephony.billing.usage.toast.queued',
    defaultMessage:
      'Queued {count, plural, one {# day} other {# days}} — data is compiling in the background, reopen in ~1 min.',
  },
  errRebuild: {
    id: 'telephony.supervisor.toast.usageRebuildFailed',
    defaultMessage: 'Could not rebuild usage data',
  },

  // ── Reusadas ──
  number: {id: 'common.field.number', defaultMessage: 'Number'},
  numbers: {id: 'telephony.billing.col.numbers', defaultMessage: 'Numbers'},
  owner: {id: 'telephony.billing.inventory.csv.owner', defaultMessage: 'Owner'},
  type: {id: 'common.field.type', defaultMessage: 'Type'},
  state: {id: 'common.field.state', defaultMessage: 'State'},
  agent: {id: 'common.field.agent', defaultMessage: 'Agent'},
  group: {id: 'common.field.group', defaultMessage: 'Group'},
  action: {id: 'common.field.action', defaultMessage: 'Action'},
  total: {id: 'common.field.total', defaultMessage: 'Total'},
  cost: {id: 'telephony.billing.column.cost', defaultMessage: 'Cost'},
  next: {id: 'common.action.next', defaultMessage: 'Next'},
  viewCalls: {id: 'telephony.billing.rebilling.viewCalls', defaultMessage: 'View calls'},
  reassign: {id: 'policies.list.page.btn.reassign', defaultMessage: 'Reassign'},

  /** Tarjetas de resumen de «Consumo por número». Se pintan como `{c.label}`. */
  cardNumbersWithActivity: {
    id: 'telephony.billing.usage.card.numbersWithActivity',
    defaultMessage: 'Numbers with activity',
  },
  cardTotalCalls: {
    id: 'telephony.supervisor.outbound.totalCalls',
    defaultMessage: 'Total calls',
  },
  cardTotalMinutes: {
    id: 'telephony.billing.usage.card.totalMinutes',
    defaultMessage: 'Total minutes',
  },
  cardTotalTwilioCost: {
    id: 'telephony.billing.usage.card.totalTwilioCost',
    defaultMessage: 'Total Twilio cost',
  },
})

/** Guion largo de celda vacía. Símbolo, no texto. */
export const EM_DASH = '—'
/** `PolicyDial` es una marca: se escribe igual en los dos idiomas. */
export const POLICY_DIAL = 'PolicyDial'
